import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Check, Loader2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '../../lib/supabase';
import { reportUiError } from '../../sentry';

type HeldEmailDetail = {
  id: string;
  kind: string;
  status: string;
  subject: string;
  body_text: string;
  proposed_send_at: string | null;
  article_title: string | null;
  article_url: string | null;
  article_collection: string | null;
  article_id: string | null;
  subscriber_id: string | null;
  source_transcript_id: string | null;
  signals: Record<string, unknown> | null;
  match_meta: Record<string, unknown> | null;
  scheduled_email_id: string | null;
  created_at: string;
  reviewed_at: string | null;
  subscribers?: { id: string; name: string | null; email: string | null; email_status: string | null } | null;
};

const HeldEmailDetailView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [row, setRow] = useState<HeldEmailDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [subjectDraft, setSubjectDraft] = useState('');
  const [bodyDraft, setBodyDraft] = useState('');
  const [proposedDraft, setProposedDraft] = useState('');

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('held_emails')
        .select(
          '*, subscribers(id, name, email, email_status)',
        )
        .eq('id', id)
        .maybeSingle();
      if (error) throw error;
      if (!data) {
        toast.error('Held email not found');
        navigate('/held-emails');
        return;
      }
      const detail = data as HeldEmailDetail;
      setRow(detail);
      setSubjectDraft(detail.subject ?? '');
      setBodyDraft(detail.body_text ?? '');
      setProposedDraft(
        detail.proposed_send_at
          ? new Date(detail.proposed_send_at).toISOString().slice(0, 16)
          : '',
      );
    } catch (e) {
      reportUiError('held_emails', 'detail', e);
      toast.error(e instanceof Error ? e.message : 'Could not load held email');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    void load();
  }, [load]);

  const saveEdits = async () => {
    if (!row || row.status !== 'held') return;
    setSaving(true);
    try {
      const proposedIso = proposedDraft
        ? new Date(proposedDraft).toISOString()
        : null;
      const { error } = await supabase
        .from('held_emails')
        .update({
          subject: subjectDraft.trim(),
          body_text: bodyDraft,
          proposed_send_at: proposedIso,
          updated_at: new Date().toISOString(),
        })
        .eq('id', row.id);
      if (error) throw error;
      toast.success('Saved');
      await load();
    } catch (e) {
      reportUiError('held_emails', 'save', e);
      toast.error(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const approve = async () => {
    if (!row || row.status !== 'held') return;
    setSaving(true);
    try {
      // Persist edits first
      const proposedIso = proposedDraft
        ? new Date(proposedDraft).toISOString()
        : null;
      const { error: updErr } = await supabase
        .from('held_emails')
        .update({
          subject: subjectDraft.trim(),
          body_text: bodyDraft,
          proposed_send_at: proposedIso,
          updated_at: new Date().toISOString(),
        })
        .eq('id', row.id);
      if (updErr) throw updErr;

      const { data: scheduledId, error } = await supabase.rpc('promote_held_email', {
        p_held_email_id: row.id,
      });
      if (error) throw error;
      toast.success('Promoted to scheduled send');
      if (typeof scheduledId === 'string') {
        // Stay on detail to show approved state
      }
      await load();
    } catch (e) {
      reportUiError('held_emails', 'approve', e);
      toast.error(e instanceof Error ? e.message : 'Approve failed');
    } finally {
      setSaving(false);
    }
  };

  const dismiss = async () => {
    if (!row || row.status !== 'held') return;
    setSaving(true);
    try {
      const { error } = await supabase.rpc('dismiss_held_email', {
        p_held_email_id: row.id,
      });
      if (error) throw error;
      toast.success('Dismissed');
      await load();
    } catch (e) {
      reportUiError('held_emails', 'dismiss', e);
      toast.error(e instanceof Error ? e.message : 'Dismiss failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !row) {
    return (
      <div className="flex justify-center py-24">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  const signals = row.signals ?? {};
  const isHeld = row.status === 'held';

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate('/held-emails')}
          className="inline-flex items-center gap-1 text-sm text-gray-600 dark:text-gray-300 hover:text-emerald-600"
        >
          <ArrowLeft className="w-4 h-4" />
          Held emails
        </button>
        <span className="inline-flex px-2 py-0.5 rounded-md text-xs font-medium bg-gray-100 dark:bg-gray-700 capitalize">
          {row.status}
        </span>
        <span className="text-xs text-gray-500">{row.kind}</span>
      </div>

      <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-2 bg-white dark:bg-gray-900">
        <div className="text-sm text-gray-500">To</div>
        <div className="font-medium text-gray-900 dark:text-white">
          {row.subscribers?.name || '—'}{' '}
          <span className="text-gray-500 font-normal">
            ({row.subscribers?.email || 'no email'})
          </span>
        </div>
        {row.subscriber_id ? (
          <Link
            to={`/subscribers/${row.subscriber_id}`}
            className="text-xs text-emerald-600 hover:underline"
          >
            Open connection
          </Link>
        ) : null}
        {row.source_transcript_id ? (
          <div>
            <Link
              to={`/transcripts/${row.source_transcript_id}`}
              className="text-xs text-emerald-600 hover:underline"
            >
              Source transcript
            </Link>
          </div>
        ) : null}
        {row.scheduled_email_id ? (
          <div className="text-xs text-gray-500">
            Scheduled email id: {row.scheduled_email_id}
          </div>
        ) : null}
      </div>

      <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-3 bg-white dark:bg-gray-900">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Signals</h2>
        <dl className="grid gap-2 text-sm">
          <div>
            <dt className="text-xs text-gray-500">Focus</dt>
            <dd className="capitalize">
              {String(signals.focus_mode || '—').replace('_', ' ')}
              {signals.focus_subject ? ` · ${String(signals.focus_subject)}` : ''}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500">What&apos;s happening now</dt>
            <dd className="text-gray-900 dark:text-gray-100">{String(signals.what_now || '—')}</dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500">If unaddressed</dt>
            <dd className="text-gray-900 dark:text-gray-100">{String(signals.if_unaddressed || '—')}</dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500">Long-term</dt>
            <dd className="text-gray-900 dark:text-gray-100">{String(signals.long_term || '—')}</dd>
          </div>
          <div className="flex flex-wrap gap-4">
            <div>
              <dt className="text-xs text-gray-500">Impact at</dt>
              <dd>{String(signals.impact_at || '—')}</dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">Follow-up days</dt>
              <dd>{String(signals.follow_up_in_days ?? '—')}</dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">Confidence</dt>
              <dd className="capitalize">{String(signals.confidence || '—')}</dd>
            </div>
          </div>
        </dl>
        {row.article_url ? (
          <p className="text-sm pt-2 border-t border-gray-100 dark:border-gray-800">
            Matched:{' '}
            <a
              href={row.article_url}
              target="_blank"
              rel="noreferrer"
              className="text-emerald-600 hover:underline"
            >
              {row.article_title || row.article_url}
            </a>
          </p>
        ) : null}
      </div>

      <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-3 bg-white dark:bg-gray-900">
        <label className="block text-xs font-medium text-gray-500">Subject</label>
        <input
          value={subjectDraft}
          onChange={(e) => setSubjectDraft(e.target.value)}
          disabled={!isHeld || saving}
          className="w-full px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 disabled:opacity-60"
        />
        <label className="block text-xs font-medium text-gray-500">Proposed send</label>
        <input
          type="datetime-local"
          value={proposedDraft}
          onChange={(e) => setProposedDraft(e.target.value)}
          disabled={!isHeld || saving}
          className="w-full max-w-xs px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 disabled:opacity-60"
        />
        <label className="block text-xs font-medium text-gray-500">Body (plain text)</label>
        <textarea
          value={bodyDraft}
          onChange={(e) => setBodyDraft(e.target.value)}
          disabled={!isHeld || saving}
          rows={14}
          className="w-full px-3 py-2 text-sm font-mono rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 disabled:opacity-60"
        />
      </div>

      {isHeld ? (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => void saveEdits()}
            disabled={saving}
            className="px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 disabled:opacity-50"
          >
            Save edits
          </button>
          <button
            type="button"
            onClick={() => void approve()}
            disabled={saving}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Approve → schedule
          </button>
          <button
            type="button"
            onClick={() => void dismiss()}
            disabled={saving}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-md border border-red-300 text-red-700 dark:text-red-300 disabled:opacity-50"
          >
            <XCircle className="w-4 h-4" />
            Dismiss
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default HeldEmailDetailView;
